import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Loader2, CheckCircle2, AlertCircle, KeyRound } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SetPassword = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') || '';

  const [validating, setValidating] = useState(true);
  const [valid, setValid] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');

  const [pwd, setPwd] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) {
      setValidating(false);
      setErrorMsg('No token in the link. Ask your administrator to re-send the invite.');
      return;
    }
    (async () => {
      try {
        const res = await axios.get(`${API}/auth/validate-set-password-token/${encodeURIComponent(token)}`);
        if (res.data?.valid) {
          setValid(true);
          setEmail(res.data.email || '');
          setName(res.data.name || '');
        } else {
          const reasonMap = {
            not_found: 'This link is invalid. It may have been replaced by a newer invite — please use the most recent email.',
            used: 'This link has already been used. Sign in with the password you set, or ask your administrator to reset it again.',
            expired: 'This link has expired. Ask your administrator to send a new invite.',
            user_missing: 'The associated account no longer exists. Please contact your administrator.',
          };
          setErrorMsg(reasonMap[res.data?.reason] || 'This link cannot be used.');
        }
      } catch (e) {
        setErrorMsg(e.response?.data?.detail || 'Could not validate the link. Please try again.');
      } finally {
        setValidating(false);
      }
    })();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (pwd.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    if (pwd !== confirm) {
      toast.error('Passwords do not match');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/auth/set-password`, { token, password: pwd });
      setDone(true);
      toast.success('Password set successfully. Redirecting to login…');
      setTimeout(() => navigate('/login', { replace: true }), 1800);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to set password');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-50 via-white to-amber-50 px-4 py-10">
      <Card data-testid="set-password-card" className="w-full max-w-md border-border shadow-lg">
        <CardHeader className="border-b border-border/50">
          <CardTitle className="font-heading text-2xl flex items-center gap-2">
            <KeyRound className="h-6 w-6 text-accent" />
            {done ? 'Password set' : 'Set your password'}
          </CardTitle>
          <CardDescription>
            MPL Finance Console
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-6 space-y-4">
          {validating && (
            <div className="flex items-center gap-2 text-muted-foreground text-sm" data-testid="set-password-validating">
              <Loader2 className="h-4 w-4 animate-spin" />
              Verifying your link…
            </div>
          )}

          {!validating && !valid && (
            <div
              data-testid="set-password-error"
              className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive"
            >
              <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <div>{errorMsg}</div>
            </div>
          )}

          {!validating && valid && !done && (
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="set-password-form">
              <div className="rounded-md bg-muted/40 border p-3 text-sm">
                <div className="text-muted-foreground text-xs uppercase tracking-wide">Account</div>
                <div className="font-medium">{name}</div>
                <div className="text-sm text-muted-foreground">{email}</div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="new-password">New password</Label>
                <Input
                  id="new-password"
                  type="password"
                  value={pwd}
                  onChange={(e) => setPwd(e.target.value)}
                  placeholder="At least 8 characters"
                  autoFocus
                  autoComplete="new-password"
                  data-testid="set-password-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirm-password">Confirm password</Label>
                <Input
                  id="confirm-password"
                  type="password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  placeholder="Type the password again"
                  autoComplete="new-password"
                  data-testid="set-password-confirm"
                />
              </div>

              <Button
                type="submit"
                disabled={submitting || pwd.length < 8 || pwd !== confirm}
                className="w-full"
                data-testid="set-password-submit"
              >
                {submitting ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Saving…</>
                ) : (
                  'Set password & sign in'
                )}
              </Button>
            </form>
          )}

          {done && (
            <div
              data-testid="set-password-success"
              className="flex items-start gap-2 rounded-md border border-emerald-300 bg-emerald-50 p-3 text-sm text-emerald-800"
            >
              <CheckCircle2 className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <div>Password set successfully. Taking you to the login page…</div>
            </div>
          )}

          <div className="pt-2 text-center text-xs text-muted-foreground">
            <Link to="/login" className="hover:text-foreground underline-offset-4 hover:underline">
              Back to login
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SetPassword;
